import { HistoryEntry, HistoryContext, UserRole } from './types';

export interface SearchQuery {
  text: string;
  contexts: HistoryContext[];
  filters: SearchFilters;
  userRole: UserRole;
  pagination: PaginationOptions;
}

export interface SearchFilters {
  dateRange?: DateRange;
  eventTypes?: string[];
  users?: string[];
  entityIds?: string[];
  customFilters?: Record<string, any>;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export interface PaginationOptions {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface SearchResults {
  results: SearchResult[];
  totalCount: number;
  facets: SearchFacets;
  suggestions: string[];
  executionTime: number;
}

export interface SearchResult {
  entry: HistoryEntry;
  score: number;
  highlights: SearchHighlight[];
  context: HistoryContext;
}

export interface SearchHighlight {
  field: string;
  fragments: string[];
}

export interface SearchFacets {
  contexts: Record<HistoryContext, number>;
  eventTypes: Record<string, number>;
  users: Record<string, number>;
  dateRanges: Record<string, number>;
}

export interface Suggestion {
  text: string;
  type: 'query' | 'filter' | 'entity';
  context?: HistoryContext;
  score: number;
}

export class SearchEngine {
  private static instance: SearchEngine;
  private searchIndex: Map<string, HistoryEntry> = new Map();
  private invertedIndex: Map<string, Set<string>> = new Map();

  constructor() {
    this.initializeIndex();
  }

  public static getInstance(): SearchEngine {
    if (!SearchEngine.instance) {
      SearchEngine.instance = new SearchEngine();
    }
    return SearchEngine.instance;
  }

  private initializeIndex(): void {
    // Initialize search index structures
    this.searchIndex.clear();
    this.invertedIndex.clear();
  }

  async search(query: SearchQuery): Promise<SearchResults> {
    const startTime = Date.now();
    
    try {
      // Validate query
      this.validateQuery(query);
      
      // Apply role-based filtering
      const allowedContexts = this.getAllowedContexts(query.userRole);
      const filteredContexts = query.contexts.filter(ctx => allowedContexts.includes(ctx));
      
      let results: SearchResult[] = [];
      
      // If we have text, perform text search
      if (query.text && query.text.trim()) {
        results = await this.performTextSearch(query.text, filteredContexts);
      } else {
        // If no text but we have filters, get all entries from allowed contexts
        results = this.getAllEntriesFromContexts(filteredContexts);
      }
      
      // Apply filters
      const filteredResults = this.applyFilters(results, query.filters);
      
      // Apply role-based access control
      const accessControlledResults = this.applyAccessControl(filteredResults, query.userRole);
      
      // Sort and paginate
      const sortedResults = this.sortResults(accessControlledResults, query.pagination);
      const paginatedResults = this.paginateResults(sortedResults, query.pagination);
      
      // Generate facets
      const facets = this.generateFacets(accessControlledResults);
      
      // Generate suggestions
      const suggestions = await this.generateSuggestions(query.text, filteredContexts);
      
      const executionTime = Date.now() - startTime;
      
      return {
        results: paginatedResults,
        totalCount: accessControlledResults.length,
        facets,
        suggestions,
        executionTime
      };
    } catch (error) {
      console.error('Search error:', error);
      throw new Error(`Search failed: ${error.message}`);
    }
  }

  async suggest(partial: string, context?: HistoryContext): Promise<Suggestion[]> {
    const suggestions: Suggestion[] = [];
    
    // Query suggestions based on search history
    const queryMatches = this.findQuerySuggestions(partial);
    suggestions.push(...queryMatches);
    
    // Entity suggestions
    const entityMatches = this.findEntitySuggestions(partial, context);
    suggestions.push(...entityMatches);
    
    // Filter suggestions
    const filterMatches = this.findFilterSuggestions(partial, context);
    suggestions.push(...filterMatches);
    
    // Sort by relevance score
    return suggestions
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
  }

  async indexEntry(entry: HistoryEntry): Promise<void> {
    try {
      // Add to main index
      this.searchIndex.set(entry.id, entry);
      
      // Update inverted index
      const searchableText = this.extractSearchableText(entry);
      const tokens = this.tokenize(searchableText);
      
      tokens.forEach(token => {
        if (!this.invertedIndex.has(token)) {
          this.invertedIndex.set(token, new Set());
        }
        this.invertedIndex.get(token)!.add(entry.id);
      });
      
      console.log(`Indexed entry ${entry.id} with ${tokens.length} tokens`);
    } catch (error) {
      console.error(`Failed to index entry ${entry.id}:`, error);
      throw error;
    }
  }

  async reindex(context?: HistoryContext): Promise<void> {
    try {
      console.log(`Starting reindex${context ? ` for context: ${context}` : ''}`);
      
      if (context) {
        // Reindex specific context
        const contextEntries = Array.from(this.searchIndex.values())
          .filter(entry => entry.context === context);
        
        // Remove old entries from inverted index
        this.removeFromInvertedIndex(contextEntries);
        
        // Re-add entries
        for (const entry of contextEntries) {
          await this.indexEntry(entry);
        }
      } else {
        // Full reindex
        this.initializeIndex();
        // In a real implementation, this would fetch all entries from the database
        console.log('Full reindex completed');
      }
    } catch (error) {
      console.error('Reindex failed:', error);
      throw error;
    }
  }

  private validateQuery(query: SearchQuery): void {
    const hasText = query.text?.trim();
    const hasFilters = query.filters && (
      query.filters.dateRange ||
      (query.filters.eventTypes && query.filters.eventTypes.length > 0) ||
      (query.filters.users && query.filters.users.length > 0) ||
      (query.filters.entityIds && query.filters.entityIds.length > 0) ||
      (query.filters.customFilters && Object.keys(query.filters.customFilters).length > 0)
    );
    
    if (!hasText && !hasFilters) {
      throw new Error('Query must contain either text or filters');
    }
    
    if (query.pagination.limit > 1000) {
      throw new Error('Pagination limit cannot exceed 1000');
    }
  }

  private getAllowedContexts(userRole: UserRole): HistoryContext[] {
    const roleContextMap: Record<UserRole, HistoryContext[]> = {
      customer: ['ticket', 'campaign'],
      agent: ['ticket', 'billing', 'campaign'],
      manager: ['ticket', 'billing', 'template', 'campaign'],
      admin: ['ticket', 'billing', 'template', 'campaign', 'system']
    };
    
    return roleContextMap[userRole] || [];
  }

  private getAllEntriesFromContexts(contexts: HistoryContext[]): SearchResult[] {
    const results: SearchResult[] = [];
    
    const entries = Array.from(this.searchIndex.values());
    for (const entry of entries) {
      if (contexts.includes(entry.context)) {
        results.push({
          entry,
          score: 1, // Default score for non-text searches
          highlights: [],
          context: entry.context
        });
      }
    }
    
    return results;
  }

  private async performTextSearch(text: string, contexts: HistoryContext[]): Promise<SearchResult[]> {
    if (!text.trim()) {
      return [];
    }
    
    const tokens = this.tokenize(text.toLowerCase());
    const matchingEntryIds = new Set<string>();
    
    // Find entries that match any token
    tokens.forEach(token => {
      const entryIds = this.invertedIndex.get(token);
      if (entryIds) {
        entryIds.forEach(id => matchingEntryIds.add(id));
      }
    });
    
    // Convert to SearchResult objects with scoring
    const results: SearchResult[] = [];
    
    const entryIds = Array.from(matchingEntryIds);
    for (const entryId of entryIds) {
      const entry = this.searchIndex.get(entryId);
      if (entry && contexts.includes(entry.context)) {
        const score = this.calculateRelevanceScore(entry, tokens);
        const highlights = this.generateHighlights(entry, tokens);
        
        results.push({
          entry,
          score,
          highlights,
          context: entry.context
        });
      }
    }
    
    return results;
  }

  private applyFilters(results: SearchResult[], filters: SearchFilters): SearchResult[] {
    return results.filter(result => {
      const entry = result.entry;
      
      // Date range filter
      if (filters.dateRange) {
        const entryDate = new Date(entry.timestamp);
        if (entryDate < filters.dateRange.start || entryDate > filters.dateRange.end) {
          return false;
        }
      }
      
      // Event type filter
      if (filters.eventTypes && filters.eventTypes.length > 0) {
        if (!filters.eventTypes.includes(entry.type)) {
          return false;
        }
      }
      
      // User filter
      if (filters.users && filters.users.length > 0) {
        if (!filters.users.includes(entry.userId)) {
          return false;
        }
      }
      
      // Entity ID filter
      if (filters.entityIds && filters.entityIds.length > 0) {
        if (!filters.entityIds.includes(entry.entityId)) {
          return false;
        }
      }
      
      // Custom filters
      if (filters.customFilters) {
        for (const [key, value] of Object.entries(filters.customFilters)) {
          if (entry.data[key] !== value) {
            return false;
          }
        }
      }
      
      return true;
    });
  }

  private applyAccessControl(results: SearchResult[], userRole: UserRole): SearchResult[] {
    // Apply role-based filtering to results
    return results.filter(result => {
      const entry = result.entry;
      
      switch (userRole) {
        case 'customer':
          // Customers can only see their own data
          return entry.userId === 'current-user-id'; // In real app, get from auth context
        case 'agent':
          // Agents can see assigned tickets and general billing info
          return true; // More complex logic would be implemented here
        case 'manager':
        case 'admin':
          // Managers and admins can see everything
          return true;
        default:
          return false;
      }
    });
  }

  private sortResults(results: SearchResult[], pagination: PaginationOptions): SearchResult[] {
    const sortBy = pagination.sortBy || 'score';
    const sortOrder = pagination.sortOrder || 'desc';
    
    return results.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'score':
          comparison = a.score - b.score;
          break;
        case 'timestamp':
          comparison = new Date(a.entry.timestamp).getTime() - new Date(b.entry.timestamp).getTime();
          break;
        case 'type':
          comparison = a.entry.type.localeCompare(b.entry.type);
          break;
        default:
          comparison = a.score - b.score;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }

  private paginateResults(results: SearchResult[], pagination: PaginationOptions): SearchResult[] {
    const start = (pagination.page - 1) * pagination.limit;
    const end = start + pagination.limit;
    return results.slice(start, end);
  }

  private generateFacets(results: SearchResult[]): SearchFacets {
    const facets: SearchFacets = {
      contexts: {} as Record<HistoryContext, number>,
      eventTypes: {},
      users: {},
      dateRanges: {}
    };
    
    results.forEach(result => {
      const entry = result.entry;
      
      // Context facets
      facets.contexts[entry.context] = (facets.contexts[entry.context] || 0) + 1;
      
      // Event type facets
      facets.eventTypes[entry.type] = (facets.eventTypes[entry.type] || 0) + 1;
      
      // User facets
      facets.users[entry.userName] = (facets.users[entry.userName] || 0) + 1;
      
      // Date range facets (simplified)
      const dateKey = new Date(entry.timestamp).toISOString().split('T')[0];
      facets.dateRanges[dateKey] = (facets.dateRanges[dateKey] || 0) + 1;
    });
    
    return facets;
  }

  private async generateSuggestions(text: string, _contexts: HistoryContext[]): Promise<string[]> {
    // Simple suggestion generation based on common terms
    const suggestions: string[] = [];
    
    if (text.length < 2) {
      return suggestions;
    }
    
    // Find similar terms in the index
    const tokens = Array.from(this.invertedIndex.keys());
    const matchingTokens = tokens.filter(token => 
      token.includes(text.toLowerCase()) && token !== text.toLowerCase()
    );
    
    return matchingTokens.slice(0, 5);
  }

  private findQuerySuggestions(partial: string): Suggestion[] {
    // In a real implementation, this would use search history
    const commonQueries = [
      'status changed',
      'payment failed',
      'template updated',
      'ticket created',
      'user login'
    ];
    
    return commonQueries
      .filter(query => query.toLowerCase().includes(partial.toLowerCase()))
      .map(query => ({
        text: query,
        type: 'query' as const,
        score: 0.8
      }));
  }

  private findEntitySuggestions(partial: string, context?: HistoryContext): Suggestion[] {
    const suggestions: Suggestion[] = [];
    
    // Find matching entity IDs or names
    const entries = Array.from(this.searchIndex.values());
    for (const entry of entries) {
      if (context && entry.context !== context) continue;
      
      if (entry.entityId.toLowerCase().includes(partial.toLowerCase()) ||
          entry.userName.toLowerCase().includes(partial.toLowerCase())) {
        suggestions.push({
          text: entry.entityId,
          type: 'entity',
          context: entry.context,
          score: 0.6
        });
      }
    }
    
    return suggestions.slice(0, 5);
  }

  private findFilterSuggestions(partial: string, context?: HistoryContext): Suggestion[] {
    const filterSuggestions = [
      'type:',
      'user:',
      'date:',
      'status:',
      'priority:'
    ];
    
    return filterSuggestions
      .filter(filter => filter.includes(partial.toLowerCase()))
      .map(filter => ({
        text: filter,
        type: 'filter' as const,
        context,
        score: 0.4
      }));
  }

  private extractSearchableText(entry: HistoryEntry): string {
    const parts = [
      entry.title,
      entry.description,
      entry.type,
      entry.userName,
      JSON.stringify(entry.data)
    ];
    
    return parts.filter(Boolean).join(' ');
  }

  private tokenize(text: string): string[] {
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(token => token.length > 2);
  }

  private calculateRelevanceScore(entry: HistoryEntry, queryTokens: string[]): number {
    const searchableText = this.extractSearchableText(entry).toLowerCase();
    let score = 0;
    
    queryTokens.forEach(token => {
      const occurrences = (searchableText.match(new RegExp(token, 'g')) || []).length;
      score += occurrences;
      
      // Boost score for title matches
      if (entry.title.toLowerCase().includes(token)) {
        score += 2;
      }
      
      // Boost score for exact type matches
      if (entry.type.toLowerCase() === token) {
        score += 3;
      }
    });
    
    // Recency boost
    const daysSinceCreation = (Date.now() - new Date(entry.timestamp).getTime()) / (1000 * 60 * 60 * 24);
    const recencyBoost = Math.max(0, 1 - daysSinceCreation / 365);
    score += recencyBoost;
    
    return score;
  }

  private generateHighlights(entry: HistoryEntry, queryTokens: string[]): SearchHighlight[] {
    const highlights: SearchHighlight[] = [];
    
    // Highlight in title
    const titleFragments = this.highlightText(entry.title, queryTokens);
    if (titleFragments.length > 0) {
      highlights.push({
        field: 'title',
        fragments: titleFragments
      });
    }
    
    // Highlight in description
    const descriptionFragments = this.highlightText(entry.description, queryTokens);
    if (descriptionFragments.length > 0) {
      highlights.push({
        field: 'description',
        fragments: descriptionFragments
      });
    }
    
    return highlights;
  }

  private highlightText(text: string, queryTokens: string[]): string[] {
    let highlightedText = text;
    
    queryTokens.forEach(token => {
      const regex = new RegExp(`(${token})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
    });
    
    // Return fragments around highlights
    if (highlightedText.includes('<mark>')) {
      return [highlightedText];
    }
    
    return [];
  }

  private removeFromInvertedIndex(entries: HistoryEntry[]): void {
    entries.forEach(entry => {
      const searchableText = this.extractSearchableText(entry);
      const tokens = this.tokenize(searchableText);
      
      tokens.forEach(token => {
        const entryIds = this.invertedIndex.get(token);
        if (entryIds) {
          entryIds.delete(entry.id);
          if (entryIds.size === 0) {
            this.invertedIndex.delete(token);
          }
        }
      });
    });
  }
}